import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Layers, Check } from 'lucide-react';
import { ExportXlsxButton } from '../shared/ExportXlsxButton';
import { useConductRiskInvestigationSummary } from '../../../api/hooks/useConductRiskInvestigationSummary';
import { fetchExportConductRiskInvestigationSummaryXlsx } from '../../../api/functionApi';
import type { InvestigationSummaryRow, InvestigationInputStatus } from '../../../api/apiTypesConductRisk';

/* ─── Config ─────────────────────────────────────────────── */
// Etichette leggibili degli input-driver (chiavi della mappa `inputs`).
const INPUT_LABELS: Record<string, string> = {
  Driver04: 'Driver 4 · Picchi Ricavi',
  Driver10: 'Driver 10 · Salto di Fasce',
  Driver11: 'Driver 11 · Commissioni Nette Q4',
  Driver12: 'Driver 12 · Outlier Clustering',
  Driver13: 'Driver 13 · Estensione Picchi',
  Driver14: 'Driver 14 · Incremento FNPC',
  Driver15: 'Driver 15 · Decremento FNPC',
};

const STATUS_STYLES: Record<InvestigationInputStatus, { cls: string; label: string }> = {
  calculated:            { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Calcolato' },
  partially_applicable:  { cls: 'bg-amber-50 text-amber-700 border-amber-200',       label: 'Parziale'  },
  not_applicable:        { cls: 'bg-gray-100 text-gray-500 border-gray-200',          label: 'N/A'       },
};

// Colonne di evidenza (chiave riga + intestazione compatta).
type EvidenceKey = Exclude<keyof InvestigationSummaryRow, 'clone_uo' | 'branch_name' | 'total_evidences'>;
const BINARY_COLS: { key: EvidenceKey; head: string; group: string }[] = [
  { key: 'd10_double_jump',          head: 'Doppio',    group: 'D10' },
  { key: 'd10_triple_jump',          head: 'Triplo',    group: 'D10' },
  { key: 'd10_quadruple_jump',       head: 'Quadr.',    group: 'D10' },
  { key: 'd11_commissions_increase', head: 'Comm. Q4',  group: 'D11' },
  { key: 'd12_outlier',              head: 'Outlier',   group: 'D12' },
  { key: 'd04_q4_spike',             head: 'Picco Q4',  group: 'D04' },
  { key: 'd13_q1_spike',             head: 'Q1',        group: 'D13' },
  { key: 'd13_q2_spike',             head: 'Q2',        group: 'D13' },
  { key: 'd13_q3_spike',             head: 'Q3',        group: 'D13' },
  { key: 'd13_december_spike',       head: 'Dic.',      group: 'D13' },
];
const COUNT_COLS: { key: EvidenceKey; head: string; group: string }[] = [
  { key: 'd14_gestori_count', head: 'Gestori', group: 'D14' },
  { key: 'd15_gestori_count', head: 'Gestori', group: 'D15' },
];

const EvidenceDot = ({ on }: { on: boolean }) =>
  on ? (
    <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-amber-100 text-amber-700 border border-amber-200">
      <Check className="w-3 h-3" strokeWidth={3} />
    </span>
  ) : (
    <span className="text-gray-300">—</span>
  );

/* ─── Main ───────────────────────────────────────────────── */
const InvestigationSummaryContainer = ({ reportId }: { reportId: string }) => {
  const { data, loading, error } = useConductRiskInvestigationSummary(reportId);

  const rows = useMemo<InvestigationSummaryRow[]>(() => data?.rows ?? [], [data]);

  const totalEvidences = useMemo(
    () => rows.reduce((s, r) => s + r.total_evidences, 0),
    [rows],
  );

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per il riepilogo investigation.</div>;
  }

  const inputEntries = Object.entries(data.inputs) as [string, InvestigationInputStatus][];

  return (
    <div className="p-6 space-y-5">

      {/* Banner + export */}
      <div className="flex items-start gap-3">
        <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100">
          <Layers className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">Aggregazione evidenze Investigation</span> · Tabella riepilogativa che aggrega, per singola filiale, le evidenze rilevate dai driver di Investigation. Ogni cella indica il numero di evidenze del relativo driver; la colonna finale riporta il totale delle evidenze per filiale.
          </p>
        </div>
        <ExportXlsxButton
          fetchBlob={() => fetchExportConductRiskInvestigationSummaryXlsx(reportId)}
          fileName={`Investigation_Summary_${data.period}`}
        />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{rows.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Filiali con evidenze</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-amber-700">{totalEvidences}</div>
          <div className="text-[11px] text-gray-400 mt-1">Evidenze totali</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{data.period}</div>
          <div className="text-[11px] text-gray-400 mt-1">Periodo</div>
        </div>
      </div>

      {/* Applicabilità input-driver */}
      {inputEntries.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-semibold text-[#1B365D] mb-2.5">Applicabilità driver di input</p>
          <div className="flex flex-wrap gap-2">
            {inputEntries.map(([key, status]) => {
              const st = STATUS_STYLES[status] ?? STATUS_STYLES.not_applicable;
              return (
                <span
                  key={key}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border ${st.cls}`}
                >
                  {INPUT_LABELS[key] ?? key}
                  <span className="opacity-60">· {st.label}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabella */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
          Nessuna filiale è stata flaggata dai driver di Investigation per questo periodo.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs font-semibold text-amber-800">
              Aggregazione evidenze per filiale — {data.period}
            </p>
            <span className="text-[10px] text-amber-700 font-semibold bg-amber-50 border border-amber-100 px-2.5 py-0.5 rounded-full">
              {rows.length} filiali
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                  <th className="text-left  px-4 py-2.5 font-semibold sticky left-0 bg-gray-50">Filiale</th>
                  {BINARY_COLS.map(c => (
                    <th key={c.key} className="text-center px-2.5 py-2.5 font-semibold whitespace-nowrap">
                      <span className="block text-amber-500">{c.group}</span>
                      {c.head}
                    </th>
                  ))}
                  {COUNT_COLS.map(c => (
                    <th key={c.key} className="text-center px-2.5 py-2.5 font-semibold whitespace-nowrap">
                      <span className="block text-amber-500">{c.group}</span>
                      {c.head}
                    </th>
                  ))}
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-500 whitespace-nowrap">Evidenze Tot.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((row, i) => (
                  <motion.tr
                    key={row.clone_uo}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.4) }}
                    className="hover:bg-amber-50/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-white">
                      <span className="font-medium text-[#1B365D]">{row.branch_name}</span>
                      <span className="block font-mono text-[10px] text-gray-400">{row.clone_uo}</span>
                    </td>
                    {BINARY_COLS.map(c => (
                      <td key={c.key} className="px-2.5 py-3 text-center">
                        <EvidenceDot on={Number(row[c.key]) > 0} />
                      </td>
                    ))}
                    {COUNT_COLS.map(c => (
                      <td key={c.key} className="px-2.5 py-3 text-center tabular-nums">
                        {Number(row[c.key]) > 0
                          ? <span className="font-semibold text-amber-700">{row[c.key]}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-[#1B365D] text-white font-bold text-[10px] tabular-nums">
                        {row.total_evidences}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestigationSummaryContainer;
